import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { DomSanitizer } from '@angular/platform-browser';
import { AuthService } from 'src/app/services/auth/auth.service';
import { environment } from '../../../environments/environment';
import { filter } from 'rxjs/operators';
import { BehaviorSubject, Subscription } from 'rxjs';

@Component({
  selector: 'app-side-bar',
  templateUrl: './side-bar.component.html',
  styleUrls: ['./side-bar.component.css']
})
export class SideBarComponent implements OnInit, OnDestroy {

  isAuth: any;
  sessionUser: any;
  activeMenu: any;
  menuOption: any;
  sidebarOpen: boolean = false;
  sidebarCollapsed: boolean = false;
  env = environment;

  // RBAC reactivo
  hasRbac: boolean = false;
  rbacModules: Set<string> = new Set();
  private rbacSubs: Subscription[] = [];

  // BehaviorSubject para comunicar el estado del sidebar a otros componentes
  private sidebarCollapsedSubject = new BehaviorSubject<boolean>(false);
  public sidebarCollapsed$ = this.sidebarCollapsedSubject.asObservable();

  constructor(
    private router: Router,
    private sanitizer: DomSanitizer,
    public _auth: AuthService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    // Suscribirse a los cambios de autenticación
    this._auth.auth$.subscribe((rep: any) => {
      this.isAuth = rep;
      this.sessionUser = rep ? this._auth.sessionUser : null;

      if (rep) {
        if (this.sessionUser?.role != 'COMPUTER_ROLE') {
          this.activeMenu = true;
        }

        // Establecer la opción de menú activa basada en la ruta actual
        this.setActiveMenuFromRoute();
      }
    });

    // Suscribirse a los cambios de opción de menú
    this._auth.menuOption.subscribe((rep: any) => {
      this.menuOption = rep;
    });

    // RBAC reactivo: suscribirse a hasRbac$ y permissions$
    this.rbacSubs.push(
      this._auth.hasRbac$.subscribe((val: boolean) => {
        this.hasRbac = val;
        this.cdr.markForCheck();
      })
    );
    this.rbacSubs.push(
      this._auth.permissions$.subscribe((perms: any[]) => {
        this.rebuildModuleAccess(perms);
        this.cdr.markForCheck();
      })
    );

    // Suscribirse a los cambios de ruta para actualizar el menú activo
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.setActiveMenuFromRoute();
      });

    // Establecer la opción de menú activa al inicializar
    this.setActiveMenuFromRoute();
  }

  ngOnDestroy(): void {
    this.rbacSubs.forEach(s => s.unsubscribe());
  }

  // Mapeo inverso: categoría backend → módulo frontend UI (sidebar)
  // contactos solo mapea a 'contactos' — cupo/planes/pagos/etc NO activan sidebar de contactos
  private readonly CATEGORY_TO_MODULE: { [backendCat: string]: string } = {
    contactos: 'contactos',
    cupo: 'contactos',
    planes: 'contactos',
    compras_financiadas: 'contactos',
    gestion_cobro: 'contactos',
    pagos: 'contactos',
    finanzas: 'finanzas',
    cobranza: 'cobranza',
    credito: 'credito',
    config: 'config',
    reportes: 'reportes',
    ventas: 'ventas',
    admin: 'admin',
    productos_compra: 'productos_compra',
  };

  private rebuildModuleAccess(perms: any[]): void {
    const modules = new Set<string>();
    for (const p of perms) {
      const code = typeof p === 'string' ? p : p?.code;
      if (!code) continue;
      if (code === '*.*.*') {
        ['contactos','ventas','finanzas','cobranza','credito','config','reportes','admin','productos_compra'].forEach(m => modules.add(m));
        break;
      }
      const backendCat = code.split('.')[0];
      if (backendCat === '*') {
        ['contactos','ventas','finanzas','cobranza','credito','config','reportes','admin','productos_compra'].forEach(m => modules.add(m));
        break;
      }
      const frontendModule = this.CATEGORY_TO_MODULE[backendCat] || backendCat;
      if (frontendModule) modules.add(frontendModule);
    }
    this.rbacModules = modules;
  }

  canAccessModule(moduleName: string): boolean {
    if (this.hasRbac) {
      return this.rbacModules.has(moduleName);
    }
    return false;
  }

  // Método para establecer la opción de menú activa basada en la ruta actual
  setActiveMenuFromRoute(): void {
    const currentUrl = this.router.url;

    // Dashboard oculto - no establecer opción de menú
    // if (currentUrl.includes('/console/dashboard')) {
    //   this.setOptionMenu(0);
    // } else
    if (currentUrl.includes('/console/create-constacts')) {
      this.setOptionMenu(1);
    } else if (currentUrl.includes('/console/ventas')) {
      this.setOptionMenu(2);
    } else if (currentUrl.includes('/console/finanzas2')) {
      this.setOptionMenu(4);
    } else if (currentUrl.includes('/console/cobranza')) {
      this.setOptionMenu(6);
    } else if (currentUrl.includes('/console/credito')) {
      this.setOptionMenu(7);
    } else if (currentUrl.includes('/console/parametros-sistema')) {
      this.setOptionMenu(8);
    } else if (currentUrl.includes('/console/export')) {
      this.setOptionMenu(9);
    } else if (currentUrl.includes('/console/caja')) {
      this.setOptionMenu(10);
    } else if (currentUrl.includes('/console/estadisticas')) {
      this.setOptionMenu(2);
    } else if (currentUrl.includes('/console/finacial-dashboard')) {
      this.setOptionMenu(3);
    } else if (currentUrl.includes('/console/credit-option')) {
      this.setOptionMenu(5);
    }
  }

  // Métodos para controlar el sidebar en móvil
  toggleSidebar(): void {
    this.sidebarOpen = !this.sidebarOpen;
  }

  closeSidebar(): void {
    this.sidebarOpen = false;
  }

  // Nuevo método para colapsar/expandir el sidebar en desktop
  toggleSidebarCollapse(): void {
    this.sidebarCollapsed = !this.sidebarCollapsed;
    // Emitir el nuevo estado a otros componentes
    this.sidebarCollapsedSubject.next(this.sidebarCollapsed);
  }

  // Método para obtener el estado actual del sidebar
  getSidebarCollapsedState(): boolean {
    return this.sidebarCollapsed;
  }

  // Método para sanitizar URLs
  sanitize(url: string) {
    return this.sanitizer.bypassSecurityTrustUrl(url);
  }

  // Método para establecer la opción de menú activa
  setOptionMenu(opt: any): void {
    this._auth.setmenuOption(opt);
    this.closeSidebar(); // Cerrar sidebar en móvil después de seleccionar
  }

  // Método para cerrar sesión
  logout(): void {
    this.router.navigate([environment.logoutRoute], {});
  }

  // Método para obtener el título de la página actual
  getPageTitle(): string {
    const url = this.router.url;

    if (url.includes('/console/create-constacts')) return 'Contactos';
    if (url.includes('/console/ventas')) return 'Ventas';
    if (url.includes('/console/finanzas2')) return 'Finanzas';
    if (url.includes('/console/finacial-dashboard')) return 'Dashboard Financiero';
    if (url.includes('/console/credit-option')) return 'Consola Administrativa';
    if (url.includes('/console/estadisticas')) return 'Estadísticas';
    if (url.includes('/console/plans')) return 'Planes';
    if (url.includes('/console/users')) return 'Usuarios';
    if (url.includes('/console/recargas')) return 'Recargas';
    if (url.includes('/console/ganancias')) return 'Ganancias';
    if (url.includes('/console/payments')) return 'Pagos';
    if (url.includes('/console/notifications')) return 'Notificaciones';
    if (url.includes('/console/cobranza')) return 'Cobranza';
    if (url.includes('/console/credito')) return 'Crédito';
    if (url.includes('/console/parametros-sistema')) return 'Parámetros del Sistema';
    if (url.includes('/console/catalog-products')) return 'Productos';
    if (url.includes('/console/cash-register-simulator')) return 'Simulador de Caja';
    // Dashboard oculto
    // if (url.includes('/console/dashboard')) return 'Dashboard';

    return 'CrediTotal';
  }
}
